import { BLEND_MODES, Container, DisplayObject, Graphics, Matrix, Rectangle, Renderer, Sprite, Texture, filters } from "pixi.js"
import { findRendererApp } from "./renderer_app"
import { ShadowPolygon } from "./building_shadow_geometry"

/** One row of block tops: the band it is drawn in (this storey's px) and the depth its pieces sort at. */
export interface BlockTopRow {
    y0: number
    y1: number
    zIndex: number
}

/** A rectangle of this storey's px. */
export interface BlockTopRect { x: number; y: number; width: number; height: number }

/** What lands on the tops, in this storey's px: shapes filled and art stamped, all in the shade's colour. */
export interface BlockTopPicture {
    polygons?: readonly ShadowPolygon[]
    /** Art drawn black-through-its-alpha through `matrix`, tinted the shade's colour. */
    stamps?: readonly { texture: Texture; matrix: Matrix }[]
    /** Rectangles filled in their own colour (what they multiply by): a roof's drip. */
    fills?: readonly (BlockTopRect & { color: number })[]
    /** Stands for the polygons and stamps: the bake is kept while it is unchanged. */
    key: string
}

/**
 * Shade on this storey's solid block tops (game-common autotile_wall.ts `block`) from what stands on the storey above: its
 * walls' and furniture's sun shadows, the contact shade at their feet, its walls' halo. A top is walked on as the storey
 * above, but it is this storey's art -- drawn a row up, sorted row by row with this storey's faces and bodies -- so the
 * storey above's shade layers cannot lie over it: they multiply inside that storey's root, which is filtered (blurred or
 * toned) whenever it is not the viewer's own, and darken nothing down here. That storey leaves the tops out of its own
 * layers, and this draws them. Baked once, softened as the storey's own layers are, between frames (a filtered bake
 * inside the frame comes out empty), kept to the tops' cells, then shown as one slice of the bake per row of tops, each
 * sorted just over its row.
 */
export class BlockTopShade {
    private slices: Sprite[] = []
    private texture?: Texture
    private key?: string
    private pending?: number
    private shown = 1

    constructor(private readonly resolution = 1) {}

    /** Draw `picture` over the rows of tops in `surface`, only on `keep`; unchanged input keeps the bake. */
    draw(surface: Container, picture: BlockTopPicture, rows: readonly BlockTopRow[], keep: readonly BlockTopRect[],
        softness: number, color: number): void {
        const key = `${softness}|${color}|${picture.key}|${rows.map(r => `${r.y0}:${r.y1}:${r.zIndex}`).join(";")}|` +
            keep.map(r => `${r.x},${r.y}`).join(";")
        if (key === this.key) {
            this.slices.forEach(slice => { if (!slice.destroyed && slice.parent !== surface) surface.addChild(slice) })
            return
        }
        this.key = key
        this.clear(false)
        const polygons = picture.polygons ?? [], stamps = picture.stamps ?? [], fills = picture.fills ?? []
        if ((!polygons.length && !stamps.length && !fills.length) || !rows.length || !keep.length) return
        const renderer = findRendererApp()?.renderer as Renderer | undefined
        if (!renderer || typeof requestAnimationFrame === "undefined") return
        this.pending = requestAnimationFrame(() => {
            this.pending = undefined
            const brush = new Container()
            const fill = new Graphics().beginFill(color, 1)
            polygons.forEach(polygon => fill.drawPolygon(polygon as number[]))
            fill.endFill()
            fills.forEach(({ x, y, width, height, color: own }) => fill.beginFill(own, 1).drawRect(x, y, width, height).endFill())
            brush.addChild(fill)
            // Borrowed art may have been destroyed since (its storey drew again): it is drawn again with the next picture.
            stamps.filter(({ texture }) => texture.valid && texture.baseTexture).forEach(({ texture, matrix }) => {
                const sprite = new Sprite(texture)
                sprite.transform.setFromMatrix(matrix)
                sprite.tint = color
                brush.addChild(sprite)
            })
            // Unsoftened (0), it is baked as drawn.
            const blur = softness > 0 ? new filters.BlurFilter(softness, 2, this.resolution) : undefined
            brush.filters = blur ? [blur] : null
            const region = brush.getLocalBounds().clone().pad(blur?.padding ?? 0)
            region.ceil()
            const soft = renderer.generateTexture(brush, { region, resolution: this.resolution })
            brush.filters = null
            brush.destroy({ children: true })
            blur?.destroy()
            // Only the tops take it: its soft edge stops at theirs, where the storey above's own layers take over.
            const kept = new Container()
            const picture = new Sprite(soft)
            picture.position.set(region.x, region.y)
            const mask = new Graphics().beginFill(0xffffff, 1)
            keep.forEach(({ x, y, width, height }) => mask.drawRect(x, y, width, height))
            mask.endFill()
            kept.addChild(picture, mask)
            picture.mask = mask
            const texture = renderer.generateTexture(kept, { region, resolution: this.resolution })
            kept.destroy({ children: true })
            soft.destroy(true)
            this.texture = texture
            rows.forEach(row => {
                const y0 = Math.max(row.y0, region.y), y1 = Math.min(row.y1, region.y + region.height)
                if (y1 <= y0) return
                const slice = new Sprite(new Texture(texture.baseTexture, new Rectangle(0, y0 - region.y, region.width, y1 - y0)))
                slice.position.set(region.x, y0)
                slice.blendMode = BLEND_MODES.MULTIPLY
                slice.zIndex = row.zIndex
                slice.alpha = this.shown
                slice.visible = this.shown > 0
                surface.addChild(slice)
                this.slices.push(slice)
            })
        })
    }

    /** How strongly the shade shows (its layer's alpha on the storey above: the sun's strength). */
    set alpha(alpha: number) {
        this.shown = alpha
        this.slices.forEach(slice => {
            slice.alpha = alpha
            slice.visible = alpha > 0
        })
    }

    /** What it has drawn into the storey's surface (handed over with the storey's art). */
    displayObjects(): DisplayObject[] {
        return this.slices.filter(slice => !slice.destroyed)
    }

    clear(forget = true): void {
        if (this.pending !== undefined) {
            cancelAnimationFrame(this.pending)
            this.pending = undefined
        }
        this.slices.forEach(slice => {
            slice.parent?.removeChild(slice)
            slice.destroy({ texture: true })
        })
        this.slices = []
        this.texture?.destroy(true)
        this.texture = undefined
        if (forget) this.key = undefined
    }
}
